import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import {
  AuthStore,
  BOOKING_TABS,
  BookingQueryParams,
  BookingStore,
  BookingSubscription,
  InstallationsStore,
  Platform,
} from '@store';

import { SubscriptionPlan, SubscriptionStatus } from '@vi-iot/client';

import moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { matTooltipSetter } from '../../shared/functions/mat-tooltip-setter/mat-tooltip-setter';

@Component({
  selector: 'app-booking-subscription-details',
  templateUrl: './booking-subscription-details.component.html',
  styleUrls: ['./booking-subscription-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingSubscriptionDetailsComponent implements OnInit, OnDestroy {
  readonly subscriptionsStatusType = SubscriptionStatus;
  readonly subscriptionsPlanType = SubscriptionPlan;

  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }
  @Input() subscription: BookingSubscription;
  @Input() isAnyActiveOrCancelledFlatRateSubscription: boolean;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  public gatewayPlatformType: Platform;
  public deviceImage: string;
  public readonly platform = Platform;

  get isProPackage(): boolean {
    return this.subscription.plan === this.subscriptionsPlanType.Pro;
  }

  get isExpired(): boolean {
    return this.subscription.status?.toLowerCase() === this.subscriptionsStatusType.expired;
  }

  constructor(
    public bookingStore: BookingStore,
    public authStore: AuthStore,
    private translation: TranslateService,
    private router: Router,
    private installationsStore: InstallationsStore
  ) {}

  ngOnInit(): void {
    if (this.subscription.installationId) {
      this.installationsStore
        .getPlatformTypeWithInstallationId(this.subscription.installationId)
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((platform) => {
          this.gatewayPlatformType = platform;
        });
    }

    this.deviceImage = this.subscription?.deviceImageUrl || '';
  }

  onProceedSubscription(deviceSerial: string): void {
    let queryParams: BookingQueryParams = {
      selectedStep: BOOKING_TABS.CHECKOUT,
      boilerSerial: deviceSerial,
      update: true,
    };

    if (this.isExpired) {
      queryParams = {
        selectedStep: BOOKING_TABS.CHECKOUT,
        boilerSerial: deviceSerial,
      };
    }
    this.router.navigate(['/booking'], {
      queryParams,
    });

    this.bookingStore.getSubscriptionsDetails(deviceSerial, !this.isExpired && !this.isProPackage);
  }

  getLeftDays(date: Date): string {
    const expirationDate = moment(date);
    const currentDate = moment();
    const leftDays = expirationDate.diff(currentDate, 'days');

    return leftDays === 1
      ? `${leftDays} ${this.translation.instant('+BOOKING.OVERVIEW.SUBSCRIPTION.DAY')}`
      : `${leftDays} ${this.translation.instant('+BOOKING.OVERVIEW.SUBSCRIPTION.DAYS')}`;
  }

  shouldShowRegistrationBanner(): boolean {
    return (
      this.subscription.status.toLowerCase() !== SubscriptionStatus.expired &&
      (this.subscription.plan === SubscriptionPlan.Pro ||
        this.subscription.plan === SubscriptionPlan.Plus) &&
      !this.subscription.registeredInstallation &&
      !this.subscription.trial
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
